/**
 * Classifications and locations — design/PROTOTYPE-SPEC.md §6, the two editors
 * that sit under the product table on Manage stock.
 *
 * Both are the same shape: a short list of labels a clinic renames occasionally
 * and adds to rarely, so both are one component with a different noun.
 *
 * Deleting is where they differ from a normal list, and the difference is the
 * point: the API refuses with a 409 when products still reference a
 * classification, and its message names the count ("Move those 4 products to
 * another classification first"). That refusal is surfaced verbatim rather than
 * replaced with a generic failure, because it already says the useful thing —
 * and because a delete that silently orphaned four products would be a far
 * worse outcome than a delete that will not happen.
 */
import { useState } from 'react';
import { c, field, sans } from '../theme.js';
import { smallButton } from './format.js';

export interface TaxonomyRow {
  id: string;
  label: string;
}

interface Props {
  title: string;
  /** Singular, lower case: "classification" / "location". Used in prose. */
  noun: string;
  blurb: string;
  rows: TaxonomyRow[];
  /** False for staff — the whole editor renders read-only. */
  mayEdit: boolean;
  onCreate: (label: string) => Promise<void>;
  onRename: (id: string, label: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function TaxonomyEditor({
  title,
  noun,
  blurb,
  rows,
  mayEdit,
  onCreate,
  onRename,
  onDelete,
}: Props) {
  const [adding, setAdding] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Every mutation goes through here so no path can leave `busy` stuck on or an
   * error from a previous attempt showing next to a successful one.
   */
  async function run(work: () => Promise<void>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await work();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not update that ${noun}`);
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontFamily: sans, fontSize: 14, fontWeight: 500, margin: '0 0 4px' }}>{title}</h3>
      <p style={{ fontSize: 13, color: c.muted, margin: '0 0 12px', lineHeight: 1.6 }}>{blurb}</p>

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 13,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          return (
            <li
              key={row.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 0',
                borderTop: `1px solid ${c.border}`,
              }}
            >
              {isEditing ? (
                <>
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    aria-label={`Rename ${row.label}`}
                    style={{ ...field, height: 36, flex: 1, maxWidth: 320 }}
                    autoFocus
                  />
                  <button
                    type="button"
                    disabled={busy || draft.trim() === ''}
                    onClick={() =>
                      void run(() => onRename(row.id, draft.trim())).then(
                        (ok) => ok && setEditingId(null),
                      )
                    }
                    style={{ ...smallButton, opacity: busy || !draft.trim() ? 0.5 : 1 }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingId(null)}
                    style={{ ...smallButton, border: 'none', color: c.muted }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontSize: 14 }}>{row.label}</span>
                  {mayEdit && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(row.id);
                          setDraft(row.label);
                          setError(null);
                        }}
                        style={smallButton}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void run(() => onDelete(row.id))}
                        style={{ ...smallButton, color: c.danger }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      {mayEdit && (
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            placeholder={`New ${noun}`}
            aria-label={`New ${noun}`}
            style={{ ...field, height: 38, flex: 1, maxWidth: 320 }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              // Never a dead button: say what is missing.
              if (adding.trim() === '') {
                setError(`Type a name for the new ${noun} first.`);
                return;
              }
              void run(() => onCreate(adding.trim())).then((ok) => ok && setAdding(''));
            }}
            style={{ ...smallButton, height: 38, opacity: busy ? 0.5 : 1 }}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}
